import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import {
  cellSummary,
  isCellEmpty,
  restLabel,
  adherencePercent,
  latestLoggedWeek,
  trendLabel,
  phaseLabel,
  weekColumns,
  finishOutcome,
} from './phaseGrid';
import { ZOOM, ZOOM_LEVELS, zoomHref } from './weekBuild';
// The zoom ladder's styles live with the build surface it came from,
// rather than being copied into a second stylesheet that would drift.
import '../styles/WeekBuildPage.css';
import '../styles/PhaseGridPage.css';

// The block, as a coach reads it.
//
// Two views over one phase, switched in place:
//
//   grid         every prescription x every week — did the work happen
//   progression  every movement x every week — is the load moving
//
// Both come from the server as finished projections (routes/grid.go). This
// component renders; it does not compute training numbers, so there is no second
// implementation of volume load or adherence to drift from the first.

const VIEW_GRID = 'grid';
const VIEW_PROGRESSION = 'progression';

const PhaseGridPage = () => {
  const { programId, phaseId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState(VIEW_GRID);
  const [grid, setGrid] = useState(null);
  const [progression, setProgression] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchView = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        buildApiUrl(`/programs/${programId}/phases/${phaseId}/${view}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (view === VIEW_GRID) {
        setGrid(response.data);
      } else {
        setProgression(response.data);
      }
    } catch (err) {
      // A 404 here is the deliberate response to anything the caller does not
      // own, so it is phrased as "not found" rather than "forbidden".
      setError(
        err.response?.status === 404
          ? 'This phase could not be found.'
          : 'Could not load the block. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [programId, phaseId, view, token]);

  useEffect(() => {
    fetchView();
  }, [fetchView]);

  const active = view === VIEW_GRID ? grid : progression;
  const phase = active?.phase;
  const weeks = weekColumns(phase, active?.weeks);

  return (
    <BaseLayout>
      <div className="phase-grid">
        <header className="phase-grid-header">
          <div>
            <h1>{phaseLabel(phase) || 'Block'}</h1>
            {phase && (
              <p className="phase-grid-meta">
                {phase.blockLengthWeeks} week{phase.blockLengthWeeks === 1 ? '' : 's'}
                {phase.sessionCount > 0 && ` · ${phase.sessionCount} day${phase.sessionCount === 1 ? '' : 's'}`}
              </p>
            )}
          </div>

          <div className="phase-grid-actions">
            {/* The same ladder the build surface carries. Without it here, "one control
              over one object" is two controls on two screens, and the block
              level is a place you can arrive at but not leave. */}
          <nav className="zoom-ladder" aria-label="How closely to look at this block">
            {ZOOM_LEVELS.map((level) => (
              <Link
                key={level.id}
                to={zoomHref({ programId, phaseId, level: level.id })}
                className={`zoom-rung${level.id === ZOOM.BLOCK ? ' is-current' : ''}`}
                aria-current={level.id === ZOOM.BLOCK ? 'page' : undefined}
              >
                <span className="zoom-rung-label">{level.label}</span>
                <span className="zoom-rung-asks">{level.asks}</span>
              </Link>
            ))}
          </nav>

          <div className="phase-grid-views" role="tablist" aria-label="Block view">
              <button
                type="button"
                role="tab"
                aria-selected={view === VIEW_GRID}
                className={view === VIEW_GRID ? 'active' : ''}
                onClick={() => setView(VIEW_GRID)}
              >
                Grid
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === VIEW_PROGRESSION}
                className={view === VIEW_PROGRESSION ? 'active' : ''}
                onClick={() => setView(VIEW_PROGRESSION)}
              >
                Progression
              </button>
            </div>
            <button type="button" className="secondary" onClick={() => navigate(`/programs/${programId}/edit`)}>
              Back to programme
            </button>
          </div>
        </header>

        {loading && <p className="phase-grid-status">Loading the block…</p>}
        {error && <p className="phase-grid-status error" role="alert">{error}</p>}

        {!loading && !error && view === VIEW_GRID && grid && (
          <GridView grid={grid} weeks={weeks} programId={programId} phaseId={phaseId} token={token} />
        )}
        {!loading && !error && view === VIEW_PROGRESSION && progression && (
          <ProgressionView progression={progression} weeks={weeks} />
        )}
      </div>
    </BaseLayout>
  );
};

/**
 * Calling a block done.
 *
 * # WHY IT ASKS TWICE
 *
 * There is no un-finish. Finishing a block IS the outbox row queued for the
 * programme writer, and that row is the athlete's own record of what was shared
 * about them — deleting it to allow a second attempt would quietly rewrite that
 * record. So the server is idempotent rather than reversible, and the only place
 * a mistake can be caught is before the press.
 *
 * # WHY IT SITS HERE AND NOT IN THE HEADER
 *
 * It belongs beside the figure it freezes. The header's actions row already
 * carries the zoom ladder, the view tablist and the way back, and adding a
 * fourth control there pushed the page to 422px in a 375px viewport once.
 */
const FinishBlock = ({ programId, phaseId, grid, token }) => {
  const [stage, setStage] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const percent = adherencePercent(grid.performedSets, grid.prescribedSets);

  const finish = async () => {
    setStage('working');
    setError('');
    try {
      const response = await axios.post(
        buildApiUrl(`/programs/${programId}/phases/${phaseId}/complete`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setStage('done');
    } catch (err) {
      setError(
        err.response?.status === 404
          ? 'This block could not be found.'
          : 'Could not finish the block. Please try again.'
      );
      setStage('idle');
    }
  };

  if (stage === 'done' && result) {
    const outcome = finishOutcome(result);
    return (
      <p className="phase-grid-finished" role="status">
        <strong>{outcome.recorded}</strong>
        {outcome.sharing && <span className="muted"> {outcome.sharing}</span>}
      </p>
    );
  }

  if (stage === 'idle' && !error) {
    return (
      <button type="button" className="phase-grid-finish" onClick={() => setStage('confirming')}>
        Finish block
      </button>
    );
  }

  return (
    <span className="phase-grid-confirm">
      {error ? (
        <span className="phase-grid-finish-error" role="alert">{error}</span>
      ) : (
        <span>
          Records {percent === null ? 'this block' : `${percent}% (${grid.performedSets} of ${grid.prescribedSets})`}
          {' '}against this block. It cannot be undone.
        </span>
      )}
      <button type="button" className="phase-grid-finish" disabled={stage === 'working'} onClick={finish}>
        {stage === 'working' ? 'Finishing…' : 'Confirm'}
      </button>
      <button type="button" className="phase-grid-cancel" onClick={() => { setStage('idle'); setError(''); }}>
        Cancel
      </button>
    </span>
  );
};

/**
 * What the athlete has done, led by the week they just trained.
 *
 * # WHY NOT THE BLOCK FIGURE FIRST
 *
 * A perfect first session against a four-week block reads "18% of prescribed
 * sets completed (8 of 44)" — a shortfall against three weeks nobody has had
 * time to reach. The block number is right at week four and wrong on day one,
 * so it moves to second place rather than disappearing.
 *
 * # WHY NOTHING-LOGGED IS NOT 0%
 *
 * Week-scoping alone would turn "0% (0 of 44)" into "Week 1: 0% (0 of 11)",
 * which is the same discouragement at smaller scale. A block nobody has started
 * has no adherence to report yet — the same reasoning adherencePercent already
 * applies to a block with nothing prescribed — so it says what is ahead instead
 * of scoring what has not happened.
 */
const BlockProgress = ({ grid, programId, phaseId, token }) => {
  const week = latestLoggedWeek(grid.weekAdherence);
  const blockPercent = adherencePercent(grid.performedSets, grid.prescribedSets);
  const finish = (
    <FinishBlock programId={programId} phaseId={phaseId} grid={grid} token={token} />
  );

  // No breakdown in the response at all — a server that predates it, or a
  // payload built before it existed. Report the block, which is what we have.
  // The alternative is to fall into the nothing-logged branch below and tell
  // someone with five logged sets that they have none, which is worse than an
  // unhelpfully wide denominator: it is untrue.
  const hasBreakdown = Array.isArray(grid.weekAdherence) && grid.weekAdherence.length > 0;
  if (!hasBreakdown) {
    if (blockPercent === null) return null;
    return (
      <p className="phase-grid-adherence">
        <strong>{blockPercent}%</strong> of prescribed sets completed
        <span className="muted"> ({grid.performedSets} of {grid.prescribedSets})</span>
        {finish}
      </p>
    );
  }

  if (!week) {
    const ahead = grid.weekAdherence?.[0]?.prescribedSets ?? grid.prescribedSets;
    return (
      <p className="phase-grid-adherence">
        Nothing logged in this block yet.
        {ahead > 0 && (
          <span className="muted"> Week 1 prescribes {ahead} sets.</span>
        )}
        {finish}
      </p>
    );
  }

  const weekPercent = adherencePercent(week.performedSets, week.prescribedSets);
  return (
    <p className="phase-grid-adherence">
      <strong>Week {week.week}: {weekPercent}%</strong> of prescribed sets completed
      <span className="muted"> ({week.performedSets} of {week.prescribedSets})</span>
      {blockPercent !== null && (
        <span className="muted phase-grid-block-figure">
          block so far {blockPercent}% ({grid.performedSets} of {grid.prescribedSets})
        </span>
      )}
      {finish}
    </p>
  );
};

const GridView = ({ grid, weeks, programId, phaseId, token }) => {

  if (grid.sessions.length === 0) {
    return (
      <p className="phase-grid-empty">
        No training days are attached to this block yet. Attach one from the
        programme editor and it will appear here.
      </p>
    );
  }

  return (
    <>
      <BlockProgress grid={grid} programId={programId} phaseId={phaseId} token={token} />

      {grid.sessions.map((session) => (
        <section key={session.sessionId} className="phase-grid-day">
          <h2>
            {session.name}
            <Link
              className="phase-grid-train"
              to={`/programs/${programId}/sessions/${session.sessionId}/train`}
            >
              Log this day
            </Link>
          </h2>

          {/* The table scrolls inside its own container: a 26-week block is wider
              than any screen, and the page itself must never scroll sideways. */}
          {/* tabIndex 0 so the strip can be scrolled by keyboard. A region that
                  scrolls but cannot be focused is reachable only by a pointer,
                  which leaves a 26-week block unreadable without a mouse. */}
          <div className="phase-grid-scroll" tabIndex={0} role="region" aria-label="Training grid, scrolls horizontally">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="sticky-col">Exercise</th>
                  <th scope="col">Prescribed</th>
                  {weeks.map((week) => (
                    <th scope="col" key={week}>Week {week}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {session.rows.map((row) => (
                  <tr key={row.entryId}>
                    <th scope="row" className="sticky-col">
                      <span className="series mono">{row.series}</span>
                      <span className="exercise-name">{row.exerciseName}</span>
                    </th>
                    <td className="prescribed">
                      <span className="mono">{row.sets} × {row.repScheme}</span>
                      {row.load !== undefined && row.load !== null && (
                        <span className="muted"> @ {row.load} {row.unit}</span>
                      )}
                      {row.percentOneRM != null && (
                        <span className="muted"> @ {row.percentOneRM}% 1RM</span>
                      )}
                      {row.rpe != null && <span className="muted"> @ RPE {row.rpe}</span>}
                      {row.rir != null && <span className="muted"> @ {row.rir} RIR</span>}
                      <span className="rest muted">{restLabel(row.rest, row.restRole)}</span>
                    </td>

                    {row.cells.map((cell) => (
                      <td
                        key={cell.week}
                        className={isCellEmpty(cell) ? 'cell empty' : 'cell'}
                        // An untrained week is announced as such rather than as
                        // an unlabelled blank.
                        aria-label={
                          isCellEmpty(cell)
                            ? `Week ${cell.week}: not trained`
                            : `Week ${cell.week}: ${cellSummary(cell, row.unit)}`
                        }
                      >
                        {isCellEmpty(cell)
                          ? <span className="muted">—</span>
                          : <span className="mono">{cellSummary(cell, row.unit)}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
};

const ProgressionView = ({ progression, weeks }) => {
  if (progression.exercises.length === 0) {
    return <p className="phase-grid-empty">Nothing is prescribed in this block yet.</p>;
  }

  return (
    <section className="phase-grid-day">
      {/* tabIndex 0 so the strip can be scrolled by keyboard. A region that
                  scrolls but cannot be focused is reachable only by a pointer,
                  which leaves a 26-week block unreadable without a mouse. */}
          <div className="phase-grid-scroll" tabIndex={0} role="region" aria-label="Training grid, scrolls horizontally">
        <table>
          <thead>
            <tr>
              <th scope="col" className="sticky-col">Movement</th>
              {weeks.map((week) => (
                <th scope="col" key={week}>Week {week}</th>
              ))}
              <th scope="col">Change</th>
            </tr>
          </thead>
          <tbody>
            {progression.exercises.map((row) => {
              const trend = trendLabel(row.changeKg, row.changePercent, row.weeks[0]?.unit ?? 'kg');
              return (
                <tr key={row.exerciseName}>
                  <th scope="row" className="sticky-col">{row.exerciseName}</th>
                  {row.weeks.map((week) => (
                    <td
                      key={week.week}
                      className={week.setsPerformed ? 'cell' : 'cell empty'}
                      aria-label={
                        week.setsPerformed
                          ? `Week ${week.week}: top set ${week.topSet} ${week.unit}`
                          : `Week ${week.week}: not trained`
                      }
                    >
                      {/* topSet is null for an untrained week and 0 for
                          bodyweight work — the check is against null. */}
                      {week.topSet === null || week.topSet === undefined
                        ? <span className="muted">—</span>
                        : <span className="mono">{week.topSet} {week.unit}</span>}
                    </td>
                  ))}
                  <td className={`trend ${trend.direction}`}>{trend.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PhaseGridPage;
